import React, { useMemo, useState } from 'react';
import { Pencil, FileText, Loader2 } from 'lucide-react';
import { Button } from "@/components/ui/button";

export interface ProductData {
    id?: string;
    productName: string;
    price?: number;
    orderId: string;
    productPhoto: File | null;
    purchaseInvoice: File | null;
}

interface ConfirmationPageProps {
    productData: ProductData;
    onConfirm: () => void;
    onGoBack: () => void;
    onEdit: () => void;
}

const ConfirmationPage: React.FC<ConfirmationPageProps> = ({
    productData,
    onConfirm,
    onGoBack,
    onEdit,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleConfirmSubmit = async () => {
        setIsSubmitting(true);
        await new Promise(resolve => setTimeout(resolve, 500));
        onConfirm();
    };

    // Generate a preview URL for the product photo
    const photoPreviewUrl = useMemo(() => {
        if (productData.productPhoto) {
            return URL.createObjectURL(productData.productPhoto);
        }
        return null;
    }, [productData.productPhoto]);

    return (
        <div className="tw-h-full tw-flex tw-flex-col">
            <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-6 sm:tw-px-4">

                {/* Header */}
                <div className="tw-text-center tw-mb-2">
                    <h1 className="tw-text-xl sm:tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2">
                        Confirm your product
                    </h1>
                    <p className="tw-text-gray-500 tw-text-sm sm:tw-text-base">
                        Please review the details below before continuing.
                    </p>
                </div>

                {/* Product Card */}
                <div className="tw-bg-gray-50 tw-rounded-2xl tw-overflow-hidden">
                    <div className="tw-flex tw-gap-4 tw-p-4">
                        {/* Product Photo */}
                        {photoPreviewUrl ? (
                            <div className="tw-w-28 tw-h-28 tw-rounded-xl tw-overflow-hidden tw-flex-shrink-0">
                                <img
                                    src={photoPreviewUrl}
                                    alt={productData.productName}
                                    className="tw-w-full tw-h-full tw-object-cover"
                                />
                            </div>
                        ) : (
                            <div className="tw-w-28 tw-h-28 tw-rounded-xl tw-bg-gray-200 tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center">
                                <span className="tw-text-gray-400 tw-text-xs">No photo</span>
                            </div>
                        )}

                        {/* Product Info */}
                        <div className="tw-flex tw-flex-col tw-justify-center tw-gap-1.5 tw-min-w-0">
                            <h3 className="tw-font-semibold tw-text-gray-900 tw-text-base tw-leading-tight tw-line-clamp-4">
                                {productData.productName || 'Unnamed Product'}
                            </h3>
                            <p className="tw-text-sm tw-text-gray-600">
                                Order ID: <span className="tw-font-medium">{productData.orderId}</span>
                            </p>
                        </div>
                    </div>

                    {productData.purchaseInvoice && (
                        <div className="tw-px-4 tw-pb-4">
                            <div className="tw-flex tw-items-center tw-gap-1.5 tw-bg-white tw-bg-opacity-50 tw-p-2 tw-rounded-lg tw-border tw-border-gray-200/50">
                                <FileText className="tw-w-4 tw-h-4 tw-text-gray-500 tw-flex-shrink-0" strokeWidth={1.5} />
                                <span className="tw-text-sm tw-text-gray-600 tw-truncate tw-flex-1">
                                    {productData.purchaseInvoice.name}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (productData.purchaseInvoice) {
                                            const url = URL.createObjectURL(productData.purchaseInvoice);
                                            window.open(url, '_blank');
                                        }
                                    }}
                                    className="tw-text-cyan-500 tw-text-sm tw-font-medium tw-bg-transparent tw-border-none tw-cursor-pointer tw-outline-none hover:tw-underline tw-flex-shrink-0"
                                >
                                    view
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Divider + Edit link */}
                    <div className="tw-border-t tw-border-gray-200">
                        <button
                            type="button"
                            onClick={onEdit}
                            className="tw-w-full tw-py-3 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-bg-transparent tw-border-none tw-cursor-pointer tw-outline-none hover:tw-bg-gray-100 tw-transition-colors"
                        >
                            <Pencil className="tw-w-4 tw-h-4 tw-text-cyan-500" strokeWidth={1.5} />
                            <span className="tw-text-cyan-500 tw-font-medium tw-text-sm">Edit product details</span>
                        </button>
                    </div>
                </div>

                {/* Confirmation message */}
                <p className="tw-text-center tw-text-gray-500 tw-text-sm tw-leading-relaxed tw-mt-4">
                    Once confirmed, these details will be used<br />
                    for all further steps.
                </p>

                {/* Action buttons */}
                <div className="tw-mt-auto tw-pb-4 sm:tw-pb-0 tw-flex tw-flex-col tw-items-center tw-gap-3">
                    <Button
                        onClick={handleConfirmSubmit}
                        disabled={isSubmitting}
                        className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-h-12 tw-bg-cyan-50 hover:tw-bg-cyan-100 tw-text-cyan-600 disabled:tw-text-gray-400 disabled:tw-bg-gray-100 tw-font-semibold tw-text-lg tw-rounded-lg tw-border-none tw-transition-colors"
                    >
                        {isSubmitting && <Loader2 className="tw-w-5 tw-h-5 tw-animate-spin" />}
                        Looks good, continue
                    </Button>
                    <button
                        type="button"
                        onClick={onGoBack}
                        className="tw-text-gray-700 tw-font-medium tw-text-sm tw-bg-transparent tw-border-none tw-cursor-pointer tw-outline-none tw-underline tw-underline-offset-2 hover:tw-text-gray-900 tw-pb-2"
                    >
                        Go back and edit
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmationPage;
